import { useCallback, useState } from 'react';
import {
  checkAddressForConfirmation,
  extractTypedAddressParts,
  extractTypedZip,
  getGoogleSuppliedAddressParts,
  capitalizeWords,
  GeocodedAddress,
  GoogleSuppliedAddressPart,
  AddressConfirmationCheckResult,
} from '../../util/AddressUtil';
import { LocationWizardData } from './types';
import { StepErrors } from './stepValidation';

/** State exposed by the confirmation dialog. */
export interface AddressConfirmationDialogState {
  open: boolean;
  typedAddress: string;
  suggestedAddress: string;
  /** Address parts that Google would supply if the user keeps their entry. */
  googleSuppliedParts: GoogleSuppliedAddressPart[];
}

/**
 * Checks whether a typed address needs the confirmation gate: text is
 * present but coordinates aren't resolved (not selected from the dropdown).
 * @returns `true` if confirmation is needed.
 */
export const needsAddressConfirmation = (data: LocationWizardData): boolean =>
  Boolean(data.locationAddress && data.locationAddress.trim()) &&
  (data.addressLatitude == null || data.addressLongitude == null);

/**
 * Builds the partial `LocationWizardData` update for a geocoded address and
 * the user's selection ('suggested' vs 'typed').
 * @returns The fields to merge into wizard data; both selections share
 * resolved coordinates/parts, differing only in address text and
 * `addressManuallyConfirmed`.
 */
export const buildGeocodedAddressUpdate = (
  geocoded: GeocodedAddress,
  typedAddress: string,
  selection: 'suggested' | 'typed',
): Partial<LocationWizardData> => {
  // When the supplier explicitly picks their own entry over Google's
  // suggestion, keep the street/city/state text they actually typed rather
  // than Google's parsed components (which can be incomplete or normalized
  // differently, e.g. an abbreviated street suffix or state name). Google's
  // geocode is only used here as a search key to locate those parts within
  // the supplier's own text — zip/coordinates always come from Google, since
  // there's no reliable way for the supplier to supply those via free text.
  // The supplier's own fields are run through `capitalizeWords` for a
  // consistent, professional-looking case without altering their spelling.
  const typedParts =
    selection === 'typed' ? extractTypedAddressParts(typedAddress, geocoded) : null;

  return {
    locationAddress: selection === 'suggested' ? geocoded.formattedAddress : capitalizeWords(typedAddress),
    addressLine1: typedParts ? capitalizeWords(typedParts.addressLine1) : geocoded.addressLine1,
    addressLine2: geocoded.addressLine2,
    city: typedParts ? capitalizeWords(typedParts.city) : geocoded.city,
    state: typedParts ? capitalizeWords(typedParts.state) : geocoded.state,
    zip: geocoded.zip || extractTypedZip(typedAddress),
    addressLatitude: geocoded.addressLatitude,
    addressLongitude: geocoded.addressLongitude,
    addressManuallyConfirmed: selection === 'typed',
  };
};

/**
 * The possible outcomes of the address confirmation gate:
 * - `'skipped'` — address does not need confirmation (already has coords);
 *   proceed normally.
 * - `'resolved'` — address was geocoded successfully (match); the caller
 *   should apply the returned update and proceed.
 * - `'blocked'` — an error was surfaced inline; do NOT advance.
 * - `'confirming'` — the confirmation dialog has been opened; the caller
 *   should NOT advance yet — the dialog callbacks will handle it.
 */
export type AddressGateOutcome =
  | { kind: 'skipped' }
  | { kind: 'resolved'; update: Partial<LocationWizardData> }
  | { kind: 'blocked' }
  | { kind: 'confirming' };

export interface UseAddressConfirmationGateReturn {
  /** True while the geocode call is in flight. */
  checkingAddress: boolean;
  /** Dialog state for rendering `AddressConfirmationDialog`. */
  confirmationDialog: AddressConfirmationDialogState;
  /** The geocoded address stashed while the dialog is open. */
  pendingGeocodedAddress: GeocodedAddress | null;
  /** The original typed address stashed while the dialog is open. */
  pendingTypedAddress: string;
  /**
   * Runs the async address-confirmation gate for Step 1.
   * Updates step errors inline on 'not-found'/'incomplete'.
   */
  runAddressGate: (
    data: LocationWizardData,
    setStepErrors: (errors: StepErrors) => void,
    onError: (message: string) => void,
  ) => Promise<AddressGateOutcome>;
  /** Close the dialog without confirming (Cancel). */
  closeConfirmationDialog: () => void;
  /** Clear pending state after the dialog confirm handler has applied data. */
  clearPending: () => void;
}

/**
 * Manages the async address-confirmation flow for Step 1: geocode check,
 * error surfacing, and dialog state.
 * @returns Gate state and handlers — see `UseAddressConfirmationGateReturn`.
 */
export function useAddressConfirmationGate(): UseAddressConfirmationGateReturn {
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<AddressConfirmationDialogState>({
    open: false,
    typedAddress: '',
    suggestedAddress: '',
    googleSuppliedParts: [],
  });

  // Stash the geocoded result while the dialog is open so the confirm handler
  // can build the data update without re-geocoding.
  const [pendingGeocodedAddress, setPendingGeocodedAddress] = useState<GeocodedAddress | null>(null);
  const [pendingTypedAddress, setPendingTypedAddress] = useState('');

  const runAddressGate = useCallback(
    async (
      data: LocationWizardData,
      setStepErrors: (errors: StepErrors) => void,
      onError: (message: string) => void,
    ): Promise<AddressGateOutcome> => {
      if (!needsAddressConfirmation(data)) {
        return { kind: 'skipped' };
      }

      setCheckingAddress(true);
      try {
        const result: AddressConfirmationCheckResult =
          await checkAddressForConfirmation(data.locationAddress);

        switch (result.status) {
          case 'not-found': {
            const message =
              "We couldn't find that address. Please check for typos or select a suggestion from the dropdown.";
            setStepErrors({ locationAddress: message });
            onError(message);
            return { kind: 'blocked' };
          }
          case 'incomplete': {
            const message = 'Address must contain a street number and name.';
            setStepErrors({ locationAddress: message });
            onError(message);
            return { kind: 'blocked' };
          }
          case 'missing-city-state': {
            const message =
              "Please include a city and state in your address (e.g., '1 Long Beach St, Anytown, CA').";
            setStepErrors({ locationAddress: message });
            onError(message);
            return { kind: 'blocked' };
          }
          case 'match': {
            // Google's suggestion matches what the supplier typed, so keep
            // their exact typed text as the displayed address (rather than
            // swapping in Google's canonical `formatted_address`, which can
            // differ cosmetically, e.g. abbreviations/casing) — Google's
            // geocode is used only as a search key to locate the matching
            // street/city/state within the supplier's own typed text; zip
            // and coordinates always come from Google. The supplier's own
            // fields are run through `capitalizeWords` for a consistent,
            // professional-looking case without altering their spelling.
            const typedParts = extractTypedAddressParts(data.locationAddress, result.address);
            const update: Partial<LocationWizardData> = {
              locationAddress: capitalizeWords(data.locationAddress),
              addressLine1: capitalizeWords(typedParts.addressLine1),
              addressLine2: result.address.addressLine2,
              city: capitalizeWords(typedParts.city),
              state: capitalizeWords(typedParts.state),
              zip: result.address.zip || extractTypedZip(data.locationAddress),
              addressLatitude: result.address.addressLatitude,
              addressLongitude: result.address.addressLongitude,
              addressManuallyConfirmed: false,
            };
            return { kind: 'resolved', update };
          }
          case 'needs-confirmation': {
            setPendingGeocodedAddress(result.address);
            setPendingTypedAddress(data.locationAddress);
            setConfirmationDialog({
              open: true,
              typedAddress: data.locationAddress,
              suggestedAddress: result.address.formattedAddress,
              googleSuppliedParts: getGoogleSuppliedAddressParts(data.locationAddress, result.address),
            });
            return { kind: 'confirming' };
          }
          case 'unverified-street': {
            // Google couldn't resolve the street, so there's no alternative
            // address to offer — open the dialog with no suggestion, asking
            // the supplier to confirm their own entry will be saved with
            // Google's approximate (zip/locality centroid) coordinates.
            setPendingGeocodedAddress(result.address);
            setPendingTypedAddress(data.locationAddress);
            setConfirmationDialog({
              open: true,
              typedAddress: data.locationAddress,
              suggestedAddress: '',
              googleSuppliedParts: [],
            });
            return { kind: 'confirming' };
          }
          default:
            return { kind: 'skipped' };
        }
      } finally {
        setCheckingAddress(false);
      }
    },
    [],
  );

  const closeConfirmationDialog = useCallback(() => {
    setConfirmationDialog((prev) => ({ ...prev, open: false }));
    setPendingGeocodedAddress(null);
    setPendingTypedAddress('');
  }, []);

  const clearPending = useCallback(() => {
    setPendingGeocodedAddress(null);
    setPendingTypedAddress('');
  }, []);

  return {
    checkingAddress,
    confirmationDialog,
    pendingGeocodedAddress,
    pendingTypedAddress,
    runAddressGate,
    closeConfirmationDialog,
    clearPending,
  };
}
